import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  officeGetRequest, officeUpdateRequest, officeAddActivity, officeReplyToRequest,
} from "@/lib/office.functions";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ArrowLeft, MessageSquare, Phone, Mail, FileText, Send } from "lucide-react";
import { VoysCallButton } from "@/components/voys/VoysCallButton";
import { formatEuroCents, type QuoteEstimate } from "@/lib/quote-pricing";

export const Route = createFileRoute("/_employee/medewerker/office/aanvragen/$id")({
  head: () => ({ meta: [{ title: "Aanvraag — EVG Office" }, { name: "robots", content: "noindex" }] }),
  component: RequestDetail,
});

function RequestDetail() {
  const { id } = useParams({ from: "/_employee/medewerker/office/aanvragen/$id" });
  const get = useServerFn(officeGetRequest);
  const upd = useServerFn(officeUpdateRequest);
  const addAct = useServerFn(officeAddActivity);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["office-request", id],
    queryFn: () => get({ data: { id } }),
  });

  const reply = useServerFn(officeReplyToRequest);
  const [note, setNote] = useState("");
  const [replySubject, setReplySubject] = useState("");
  const [replyBody, setReplyBody] = useState("");
  const replyMut = useMutation({
    mutationFn: (v: { subject: string; body: string }) => reply({ data: { id, ...v } }),
    onSuccess: (res: any) => {
      if (res?.ok === false) {
        toast.error("Versturen mislukt: " + (res.reason ?? "onbekende reden"));
        return;
      }
      setReplyBody("");
      setReplySubject("");
      qc.invalidateQueries({ queryKey: ["office-request", id] });
      toast.success("Antwoord verstuurd naar de klant");
    },
    onError: (e: any) => toast.error(e?.message ?? "Versturen mislukt"),
  });
  const updateMut = useMutation({ mutationFn: (v: any) => upd({ data: { id, ...v } }), onSuccess: () => qc.invalidateQueries({ queryKey: ["office-request", id] }) });
  const noteMut = useMutation({
    mutationFn: () => addAct({ data: { quote_request_id: id, type: "note", body: note } }),
    onSuccess: () => { setNote(""); qc.invalidateQueries({ queryKey: ["office-request", id] }); toast.success("Notitie toegevoegd"); },
  });

  if (isLoading) return <div className="p-8 text-center text-slate-400">Laden…</div>;
  const r = data!.request as any;
  const est = (r.estimate ?? null) as QuoteEstimate | null;

  return (
    <div className="space-y-5">
      <Link to="/medewerker/office/aanvragen" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#0b2a4a]"><ArrowLeft className="size-3" /> Terug naar aanvragen</Link>
      <div className="grid gap-5 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-4">
          <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-xl font-bold text-slate-900">{r.name}</h1>
                <p className="text-sm text-slate-500">{r.service} • {new Date(r.created_at).toLocaleString("nl-NL")}</p>
              </div>
              <select
                value={r.stage ?? "new"}
                onChange={(e) => updateMut.mutate({ stage: e.target.value })}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
              >
                {["new","contacted","qualified","quoted","won","lost"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <a href={`mailto:${r.email}`} className="flex items-center gap-2 rounded-lg bg-slate-50 p-3"><Mail className="size-4 text-slate-500" /> {r.email}</a>
              <div className="flex items-center gap-2 rounded-lg bg-slate-50 p-3">
                <a href={`tel:${r.phone}`} className="flex items-center gap-2 flex-1"><Phone className="size-4 text-slate-500" /> {r.phone}</a>
                <VoysCallButton phone={r.phone} size="sm" label="Voys" />
              </div>
              <div className="col-span-2 rounded-lg bg-slate-50 p-3 text-slate-700 whitespace-pre-wrap">{r.message ?? "—"}</div>
            </div>
            <div className="mt-4">
              <Link
                to="/medewerker/office/offertes/nieuw"
                search={{ requestId: r.id } as any}
                className="inline-flex items-center gap-2 rounded-lg bg-[#0b2a4a] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0e3463]"
              >
                <FileText className="size-4" /> Offerte opstellen
              </Link>
            </div>
          </div>

          {est && (
            <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
              <div className="flex items-baseline justify-between">
                <h2 className="font-semibold text-slate-900">
                  Automatisch opgestelde offerte
                </h2>
                <span className="text-lg font-bold text-[#0b2a4a]">
                  {formatEuroCents(est.totalCents)}
                </span>
              </div>
              <p className="mt-1 text-xs text-slate-500">
                Niet zichtbaar voor de klant · {est.cabinet} · {est.groups} groepen ·{" "}
                {est.rcds} aardlekschakelaars
                {est.includeSpd ? " · overspanningsbeveiliging" : ""}
                {est.assumedDefaults ? " · standaardaanname" : " · uit de configurator"}
              </p>
              <table className="mt-4 w-full text-sm">
                <tbody>
                  {est.lines.map((l, i) => (
                    <tr key={i} className="border-b border-slate-100 last:border-0">
                      <td className="py-2 pr-3 text-slate-700">{l.qty}× {l.label}</td>
                      <td className="py-2 text-right font-medium text-slate-900 whitespace-nowrap">
                        {formatEuroCents(l.amountCents)}
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td className="pt-3 text-xs text-slate-500">
                      Excl. btw {formatEuroCents(est.subtotalCents)} · btw{" "}
                      {formatEuroCents(est.vatCents)}
                    </td>
                    <td className="pt-3 text-right font-bold text-slate-900">
                      {formatEuroCents(est.totalCents)} incl. btw
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
            <h2 className="font-semibold text-slate-900">Antwoord aan de klant</h2>
            <p className="mt-1 text-xs text-slate-500">
              Wordt per e-mail verstuurd naar {r.email} en bewaard bij deze aanvraag.
            </p>
            <input
              value={replySubject}
              onChange={(e) => setReplySubject(e.target.value)}
              placeholder={`Over je aanvraag voor ${r.service ?? "een offerte"}`}
              className="mt-3 w-full rounded-lg border border-slate-200 p-2 text-sm"
            />
            <textarea
              value={replyBody}
              onChange={(e) => setReplyBody(e.target.value)}
              rows={6}
              placeholder="Typ hier je antwoord aan de klant…"
              className="mt-2 w-full rounded-lg border border-slate-200 p-2 text-sm"
            />
            <div className="mt-3 flex justify-end">
              <Button
                disabled={!r.email || replyBody.trim().length < 2 || replyMut.isPending}
                onClick={() =>
                  replyMut.mutate({
                    subject:
                      replySubject.trim() || `Over je aanvraag voor ${r.service ?? "een offerte"}`,
                    body: replyBody.trim(),
                  })
                }
              >
                <Send className="size-4" /> {replyMut.isPending ? "Versturen…" : "Antwoord versturen"}
              </Button>
            </div>
          </div>

          <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
            <h2 className="font-semibold text-slate-900">Activiteiten</h2>
            <div className="mt-3 flex gap-2">
              <textarea value={note} onChange={(e) => setNote(e.target.value)} className="flex-1 rounded-lg border border-slate-200 p-2 text-sm" rows={2} placeholder="Interne notitie…" />
              <Button disabled={!note.trim() || noteMut.isPending} onClick={() => noteMut.mutate()}>Toevoegen</Button>
            </div>
            <ul className="mt-4 space-y-2">
              {data!.activities.map((a: any) => (
                <li key={a.id} className="rounded-lg border border-slate-100 p-3 text-sm">
                  <div className="flex items-center gap-2 text-xs text-slate-500"><MessageSquare className="size-3" /> {a.type} • {new Date(a.created_at).toLocaleString("nl-NL")}</div>
                  <p className="mt-1 whitespace-pre-wrap">{a.body}</p>
                </li>
              ))}
              {data!.activities.length === 0 && <p className="text-sm text-slate-400">Nog geen activiteiten.</p>}
            </ul>
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
            <h3 className="text-xs font-semibold uppercase text-slate-500">Interne notities</h3>
            <textarea
              defaultValue={r.internal_notes ?? ""}
              onBlur={(e) => updateMut.mutate({ internal_notes: e.target.value })}
              className="mt-2 h-32 w-full rounded-lg border border-slate-200 p-2 text-sm"
            />
          </div>
          <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
            <h3 className="text-xs font-semibold uppercase text-slate-500">Vervolgactie</h3>
            <input
              type="datetime-local"
              defaultValue={r.follow_up_at ? new Date(r.follow_up_at).toISOString().slice(0, 16) : ""}
              onBlur={(e) => updateMut.mutate({ follow_up_at: e.target.value ? new Date(e.target.value).toISOString() : null })}
              className="mt-2 w-full rounded-lg border border-slate-200 p-2 text-sm"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
